import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, X, Eye, Clock, CheckCircle, XCircle, FileText, Phone } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PaymentApprovals = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showAttachmentModal, setShowAttachmentModal] = useState(false);
  const [approveMonths, setApproveMonths] = useState(1);
  const [rejectReason, setRejectReason] = useState('');
  const [processing, setProcessing] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    fetchRequests();
  }, [filter]);

  const fetchRequests = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const params = filter !== 'all' ? { status: filter } : {};
      const response = await axios.get(`${API}/admin/payment-requests`, {
        params,
        headers: { Authorization: `Bearer ${token}` }
      });
      setRequests(response.data);
    } catch (err) {
      console.error('Error fetching requests:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!selectedRequest) return;
    setProcessing(true);
    setError('');

    try {
      const token = localStorage.getItem('admin_token');
      await axios.put(`${API}/admin/payment-requests/${selectedRequest.id}/approve`, {
        duration_months: approveMonths
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setSuccess(`Payment approved! Subscription activated for ${approveMonths} month(s).`);
      setShowApproveModal(false);
      setSelectedRequest(null);
      setApproveMonths(1);
      fetchRequests();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to approve payment');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!selectedRequest || !rejectReason.trim()) {
      setError('Please provide a reason for rejection');
      return;
    }
    setProcessing(true);
    setError('');

    try {
      const token = localStorage.getItem('admin_token');
      await axios.put(`${API}/admin/payment-requests/${selectedRequest.id}/reject`, {
        reason: rejectReason
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setSuccess('Payment rejected and tenant deactivated.');
      setShowRejectModal(false);
      setSelectedRequest(null);
      setRejectReason('');
      fetchRequests();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to reject payment');
    } finally {
      setProcessing(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <span className="px-2 py-1 bg-amber-100 text-amber-700 text-xs font-medium rounded-full flex items-center gap-1"><Clock size={12} /> Pending</span>;
      case 'approved':
        return <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full flex items-center gap-1"><CheckCircle size={12} /> Approved</span>;
      case 'rejected':
        return <span className="px-2 py-1 bg-red-100 text-red-700 text-xs font-medium rounded-full flex items-center gap-1"><XCircle size={12} /> Rejected</span>;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-violet-200 border-t-violet-600"></div>
      </div>
    );
  }

  return (
    <div data-testid="payment-approvals-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Payment Approvals
          </h1>
          <p className="text-slate-600 mt-1">
            Review and approve customer payment submissions
          </p>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <span className="px-3 py-1.5 bg-amber-100 text-amber-700 rounded-full font-medium">
            {requests.filter(r => r.status === 'pending').length} Pending
          </span>
        </div>
      </div>

      {success && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-xl text-green-700 flex items-center gap-2">
          <CheckCircle size={20} />
          {success}
          <button onClick={() => setSuccess('')} className="ml-auto text-green-600 hover:text-green-800">
            <X size={18} />
          </button>
        </div>
      )}

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 flex items-center gap-2">
          <XCircle size={20} />
          {error}
          <button onClick={() => setError('')} className="ml-auto text-red-600 hover:text-red-800">
            <X size={18} />
          </button>
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-2">
        {['pending', 'approved', 'rejected', 'all'].map((status) => (
          <button
            key={status}
            onClick={() => setFilter(status)}
            data-testid={`filter-${status}-btn`}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              filter === status
                ? 'bg-violet-600 text-white'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            {status.charAt(0).toUpperCase() + status.slice(1)}
          </button>
        ))}
      </div>

      {/* Requests Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-700">Business</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-700">Mobile</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-700">Plan</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-700">Amount</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-700">UTR</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-700">Date</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-700">Status</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-700">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {requests.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-12 text-center text-slate-500">
                    No payment requests found
                  </td>
                </tr>
              ) : (
                requests.map((req, index) => (
                  <motion.tr
                    key={req.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="hover:bg-slate-50"
                    data-testid={`payment-row-${index}`}
                  >
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-medium text-slate-800 text-sm">{req.business_name}</p>
                        <p className="text-xs text-slate-500">{req.store_id}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <a href={`tel:${req.mobile_number}`} className="text-sm text-violet-600 hover:underline flex items-center gap-1">
                        <Phone size={12} /> {req.mobile_number}
                      </a>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm font-medium">{req.plan_name}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm font-semibold text-green-600">₹{req.amount}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs font-mono bg-slate-100 px-2 py-1 rounded">{req.utr_number}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs text-slate-600">{formatDate(req.created_at)}</span>
                    </td>
                    <td className="px-4 py-3">
                      {getStatusBadge(req.status)}
                      {req.approved_months && (
                        <span className="block text-xs text-slate-500 mt-1">{req.approved_months} months</span>
                      )}
                      {req.rejection_reason && (
                        <span className="block text-xs text-red-500 mt-1 truncate max-w-[100px]" title={req.rejection_reason}>
                          {req.rejection_reason}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        {req.attachment_url && (
                          <button
                            onClick={() => { setSelectedRequest(req); setShowAttachmentModal(true); }}
                            className="p-1.5 hover:bg-slate-100 text-slate-600 rounded-lg"
                            title="View Attachment"
                            data-testid={`view-attachment-${index}`}
                          >
                            <FileText size={16} />
                          </button>
                        )}
                        {req.status === 'pending' && (
                          <>
                            <button
                              onClick={() => { setSelectedRequest(req); setShowApproveModal(true); }}
                              className="p-1.5 hover:bg-green-50 text-green-600 rounded-lg"
                              title="Approve"
                              data-testid={`approve-${index}`}
                            >
                              <Check size={16} />
                            </button>
                            <button
                              onClick={() => { setSelectedRequest(req); setShowRejectModal(true); }}
                              className="p-1.5 hover:bg-red-50 text-red-600 rounded-lg"
                              title="Reject"
                              data-testid={`reject-${index}`}
                            >
                              <X size={16} />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </motion.tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Approve Modal */}
      <AnimatePresence>
        {showApproveModal && selectedRequest && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-xl shadow-2xl w-full max-w-md"
            >
              <div className="p-6 border-b border-slate-200">
                <h3 className="text-xl font-semibold text-green-700 flex items-center gap-2">
                  <CheckCircle size={24} />
                  Approve Payment
                </h3>
              </div>
              <div className="p-6 space-y-4">
                <div className="bg-slate-50 rounded-lg p-4">
                  <p className="text-sm text-slate-600">Business: <strong>{selectedRequest.business_name}</strong></p>
                  <p className="text-sm text-slate-600">Amount: <strong className="text-green-600">₹{selectedRequest.amount}</strong></p>
                  <p className="text-sm text-slate-600">UTR: <strong>{selectedRequest.utr_number}</strong></p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Select Subscription Duration
                  </label>
                  <div className="grid grid-cols-4 gap-2">
                    {[1, 3, 6, 12].map((months) => (
                      <button
                        key={months}
                        type="button"
                        onClick={() => setApproveMonths(months)}
                        className={`py-3 rounded-lg font-medium transition-all ${
                          approveMonths === months
                            ? 'bg-green-600 text-white'
                            : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                        }`}
                      >
                        {months} {months === 1 ? 'Month' : 'Months'}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    onClick={() => { setShowApproveModal(false); setSelectedRequest(null); }}
                    className="flex-1 px-4 py-2 border border-slate-200 rounded-lg hover:bg-slate-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleApprove}
                    disabled={processing}
                    className="flex-1 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 disabled:opacity-50"
                    data-testid="confirm-approve-btn"
                  >
                    {processing ? 'Processing...' : 'Approve & Activate'}
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Reject Modal */}
      <AnimatePresence>
        {showRejectModal && selectedRequest && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-xl shadow-2xl w-full max-w-md"
            >
              <div className="p-6 border-b border-slate-200">
                <h3 className="text-xl font-semibold text-red-700 flex items-center gap-2">
                  <XCircle size={24} />
                  Reject Payment
                </h3>
              </div>
              <div className="p-6 space-y-4">
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-sm text-red-800">
                    <strong>{selectedRequest.business_name}</strong> will be deactivated and can only see a "Contact Support" message.
                  </p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Reason for Rejection <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    rows={3}
                    placeholder="e.g., Invalid UTR number, Amount mismatch, etc."
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 resize-none"
                    data-testid="rejection-reason-input"
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    onClick={() => { setShowRejectModal(false); setSelectedRequest(null); setRejectReason(''); }}
                    className="flex-1 px-4 py-2 border border-slate-200 rounded-lg hover:bg-slate-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleReject}
                    disabled={processing || !rejectReason.trim()}
                    className="flex-1 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 disabled:opacity-50"
                    data-testid="confirm-reject-btn"
                  >
                    {processing ? 'Processing...' : 'Reject & Deactivate'}
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Attachment Modal */}
      <AnimatePresence>
        {showAttachmentModal && selectedRequest && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden"
            >
              <div className="p-4 border-b border-slate-200 flex items-center justify-between">
                <h3 className="font-semibold">Payment Screenshot - {selectedRequest.business_name}</h3>
                <button
                  onClick={() => { setShowAttachmentModal(false); setSelectedRequest(null); }}
                  className="p-2 hover:bg-slate-100 rounded-lg"
                >
                  <X size={20} />
                </button>
              </div>
              <div className="p-4 overflow-auto max-h-[70vh]">
                {selectedRequest.attachment_url ? (
                  <img 
                    src={selectedRequest.attachment_url} 
                    alt="Payment Screenshot" 
                    className="w-full rounded-lg"
                  />
                ) : (
                  <p className="text-center text-slate-500 py-8">No attachment available</p>
                )}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default PaymentApprovals;
